// Add company — the four screens of docs/design/guided-onboarding.md §5.1 that
// the existing routes support. Screen 4 of the design (properties/locations)
// needs settings routes that do not exist yet (OB-9a), so this page says so and
// stops. Nothing carrying accounting meaning is pre-selected.
//
// Create order is the brief's: POST /v1/admin/onboarding/runs (the run record)
// then POST /v1/admin/companies (register). The run route does not carry an
// opening-balances field; the choice below is deliberately not sent as if it
// were recorded anywhere. See the PR's §5 flag.

import { AdminApiError, type AdminClient } from "../api.js";
import { amVisibleDisplayText } from "../../../ingest-worker/src/am_visible_text.js";
import { clear, h, safeText } from "../dom.js";
import { failureCopy, outcomeSentence, type FailureCopy } from "../ui/failure.js";
import { capabilitySummary, kindLabel } from "../ui/plan_summary.js";
import { dateText } from "../ui/display-words.js";
import { appendReceipt, renderReceiptFailure } from "../ui/receipt_card.js";
import { navLabel } from "../ui/sentences.js";
import {
  forgetPossiblySent,
  runWrite,
  wasPossiblySent,
  type WriteOutcome,
} from "../ui/write.js";
import { operationalAccrualWritesAllowed } from "./onboarding.js";
import type {
  Company,
  CompanyBasis,
  CompanyFeaturePack,
  CompanySlug,
  CompanyType,
  OnboardingCapabilities,
  OnboardingCapabilityKind,
  OnboardingRunCreate,
  OnboardingStatus,
  StaffRole,
} from "../types.js";

type Kind = OnboardingCapabilityKind;

type SlugState = "empty" | "invalid" | "checking" | "free" | "taken" | "unknown";

interface FormState {
  kind: Kind | "";
  opsHub: boolean;
  practiceHub: boolean;
  practiceId: string;
  tenantPortal: boolean;
  moov: boolean;
  trust: boolean;
  apiKey: boolean;
  statementsDownload: boolean;
  displayName: string;
  slug: CompanySlug;
  slugEdited: boolean;
  slugState: SlugState;
  slugReason: string;
  basis: CompanyBasis | "";
  booksStart: string;
  opening: "" | "later" | "now";
  openingAck: boolean;
}

export interface AddCompanyPageOptions {
  client: AdminClient;
  root: HTMLElement;
  role: StaffRole;
  isCurrent?: () => boolean;
  /** Called after both calls succeed; opens the new company's checklist. */
  onCreated: (slug: CompanySlug) => void;
}

const KIND_CARDS: { kind: Kind; label: string; hint: string }[] = [
  {
    kind: "property",
    label: "Rental property",
    hint: "Rent, deposits, and the property's own bank accounts.",
  },
  {
    kind: "practice",
    label: "Medical practice",
    hint: "Practice income and expenses. Location codes only — never a person's clinical details.",
  },
  {
    kind: "other",
    label: "Other business",
    hint: "A company that is neither a rental property nor a medical practice.",
  },
];

const SLUG_PATTERN = /^[a-z][a-z0-9-]*$/;

/** One write namespace per mounted page: internal, never a company word. */
let addCompanyPageSeq = 0;
const SLUG_CHECK_DELAY_MS = 300;

export async function renderAddCompanyPage(opts: AddCompanyPageOptions): Promise<void> {
  const { client, root } = opts;
  const role = opts.role;
  const stillCurrent = (): boolean => opts.isCurrent?.() ?? true;
  const writePage = ++addCompanyPageSeq;
  const state: FormState = {
    kind: "",
    opsHub: false,
    practiceHub: false,
    practiceId: "",
    tenantPortal: false,
    moov: false,
    trust: false,
    apiKey: false,
    statementsDownload: false,
    displayName: "",
    slug: "",
    slugEdited: false,
    slugState: "empty",
    slugReason: "",
    basis: "",
    booksStart: "",
    opening: "",
    openingAck: false,
  };
  let step: 0 | 1 | 2 | 3 = 0;
  let companySlugs = new Set<string>();
  let draftSlugs = new Set<string>();
  /**
   * The setup record this page created, with the answers it recorded (A9-H1).
   * Null until POST /onboarding/runs succeeds. Kept so a retry can neither
   * re-register against answers the record does not hold nor register a
   * company whose setup record is missing.
   */
  let createdRun: {
    answers: ReturnType<typeof buildAnswers>;
    capabilities: OnboardingCapabilities;
  } | null = null;
  let creating = false;
  let slugSeq = 0;
  /** The raw text of the web-id box (a plain string, never a company word). */
  let slugInputValue = "";
  let slugTimer: ReturnType<typeof setTimeout> | null = null;
  let refreshNameUi: (() => void) | null = null;
  let createError: {
    face: string;
    detail: string | null;
    /** The request may have landed: the card must not claim nothing changed. */
    mayHaveLanded: boolean;
    /** The card's closing line when it differs from the shared "Nothing changed." */
    unchanged: string | null;
    /** What the person can do next. "none" = the card already names the way out. */
    next: "press" | "reload" | "back" | "none";
  } | null = null;
  // A getter, not a mirrored boolean: every paint reads the one source of
  // truth, so the lock, the sentence and the button label cannot drift apart.
  const runCreated = (): boolean => createdRun !== null;

  clear(root);
  root.append(h("p", { class: "muted" }, ["Loading company names…"]));

  const [companiesResult, draftsResult] = await Promise.allSettled([
    client.listCompanies(),
    client.listCompanyDrafts(),
  ]);
  if (!stillCurrent()) return;
  if (companiesResult.status === "fulfilled") {
    companySlugs = new Set(companiesResult.value.companies.map((row: Company) => row.slug));
  }
  if (draftsResult.status === "fulfilled") {
    draftSlugs = new Set(draftsResult.value.drafts.map((row) => row.slug));
  }
  paint();

  function paint(): void {
    refreshNameUi = null;
    clear(root);
    root.append(
      h("header", { class: "page-header" }, [
        h("h1", { class: "page-title", "data-role": "add-company-title" }, [
          navLabel("add-company"),
        ]),
        h("p", { class: "lede", "data-role": "add-company-step" }, [
          `Step ${String(step + 1)} of 4 — ${stepName(step)}`,
        ]),
      ]),
    );
    if (step === 0) paintKind();
    if (step === 1) paintName();
    if (step === 2) paintBooks();
    if (step === 3) paintReview();
  }

  function paintKind(): void {
    const host = h("section", { class: "card", "data-role": "add-company-kind" });
    host.append(h("h2", {}, ["What kind of company?"]));
    const cards = h("div", { class: "choice-cards" });
    for (const card of KIND_CARDS) {
      const button = h(
        "button",
        {
          type: "button",
          class: state.kind === card.kind ? "choice-card selected" : "choice-card",
          "data-kind": card.kind,
          "aria-pressed": state.kind === card.kind ? "true" : "false",
        },
        [h("strong", {}, [card.label]), h("span", { class: "muted" }, [card.hint])],
      );
      // Read-only once the setup record exists; the answers it holds are what
      // the company will be registered with (A9-H1).
      button.disabled = runCreated();
      button.addEventListener("click", () => {
        if (state.kind !== card.kind) {
          state.kind = card.kind;
          resetQuestionsForKind();
        }
        paint();
      });
      cards.append(button);
    }
    host.append(cards);
    if (runCreated()) host.append(setupRecordLocked(), setupRecordExit());

    if (state.kind !== "") {
      host.append(renderQuestions());
    }

    const next = h(
      "button",
      {
        type: "button",
        class: "btn btn-primary",
        "data-action": "add-company-next",
      },
      ["Continue"],
    );
    next.disabled = !kindReady();
    next.addEventListener("click", () => {
      if (!kindReady()) return;
      step = 1;
      paint();
    });
    host.append(h("div", { class: "card-actions" }, [next]));
    root.append(host);
  }

  function renderQuestions(): HTMLElement {
    const host = h("section", { class: "card", "data-role": "add-company-questions" });
    host.append(h("h2", {}, ["A few questions about this company"]));

    if (state.kind === "property") {
      host.append(
        checkboxRow(
          "opsHub",
          "Does the hub send its bills and receipts to this company?",
          "Adds the hub connection, properties, account list and receipt category map.",
          "opsHub",
        ),
        checkboxRow(
          "tenantPortal",
          "Do tenants pay through the tenant portal?",
          "Adds the portal setup and tenant-payment accounts. This also needs a wallet.",
          "tenantPortal",
        ),
        checkboxRow(
          "trust",
          "Is this a trust that only holds deposits?",
          "Adds the two trust deposit accounts. A trust does not also use the ops hub.",
          "trust",
        ),
      );
    }
    if (state.kind === "practice") {
      host.append(
        checkboxRow(
          "practiceHub",
          "Is this company connected to the Practice Hub?",
          "Adds the hub link and the practice questions.",
          "practiceHub",
        ),
      );
    }
    host.append(
      checkboxRow(
        "moov",
        "Does this company hold a wallet?",
        "Adds the wallet setting, wallet accounts and the wallet check.",
        "moov",
      ),
      checkboxRow(
        "apiKey",
        "Will another system read its reports or deposits?",
        "Adds an API key and its checklist rows.",
        "apiKey",
      ),
    );

    if (state.apiKey) {
      host.append(
        checkboxRow(
          "statementsDownload",
          "Should that system also download bank statements?",
          "Adds the statements option to the key. It is off unless you choose it.",
          "statementsDownload",
        ),
      );
    }

    if (state.kind === "practice" && state.practiceHub) {
      const id = h("input", {
        type: "text",
        class: "input",
        "data-role": "practice-hub-id",
        "aria-label": "Practice id from the Practice Hub",
        placeholder: "practice-id",
        autocomplete: "off",
      });
      id.value = state.practiceId;
      id.disabled = runCreated();
      id.addEventListener("input", () => {
        state.practiceId = id.value.trim();
      });
      host.append(
        h("label", { class: "field" }, [
          h("span", { class: "field-label" }, ["Practice id"]),
          id,
          h("span", { class: "muted" }, [
            "Required when the Practice Hub is connected.",
          ]),
        ]),
      );
    }

    host.append(
      h("p", { class: "muted", "data-role": "combination-note" }, [
        "Some answers belong together. Choosing the tenant portal also turns on the wallet; a trust cannot also use the ops hub; a hub connection needs an API key. The server also checks these.",
      ]),
    );
    return host;
  }

  function checkboxRow(
    field: "opsHub" | "tenantPortal" | "trust" | "practiceHub" | "moov" | "apiKey" | "statementsDownload",
    label: string,
    hint: string,
    role: string,
  ): HTMLElement {
    const locked = fieldLocked(field);
    const input = h("input", {
      type: "checkbox",
      "data-role": `cap-${role}`,
      "aria-label": label,
    });
    input.checked = fieldChecked(field);
    // The answers (including these capabilities) are what the setup record
    // holds, so once it exists they are read-only (A9-H1).
    input.disabled = locked || runCreated();
    input.addEventListener("change", () => {
      state[field] = input.checked;
      applyCombinationRules();
      paint();
    });
    return h("label", { class: "choice-label checkbox-row" }, [
      input,
      h("span", {}, [label]),
      h("span", { class: "muted" }, [hint]),
      locked ? h("span", { class: "muted" }, ["Required by another answer."]) : null,
    ]);
  }

  function fieldChecked(
    field: "opsHub" | "tenantPortal" | "trust" | "practiceHub" | "moov" | "apiKey" | "statementsDownload",
  ): boolean {
    if (field === "opsHub") return state.trust ? false : state.opsHub;
    if (field === "moov") return state.tenantPortal ? true : state.moov;
    if (field === "apiKey") {
      return state.opsHub || state.practiceHub ? true : state.apiKey;
    }
    if (field === "statementsDownload") return state.apiKey && state.statementsDownload;
    return state[field];
  }

  function fieldLocked(
    field: "opsHub" | "tenantPortal" | "trust" | "practiceHub" | "moov" | "apiKey" | "statementsDownload",
  ): boolean {
    if (field === "opsHub") return state.trust;
    if (field === "moov") return state.tenantPortal;
    if (field === "apiKey") return state.opsHub || state.practiceHub;
    return false;
  }

  function applyCombinationRules(): void {
    if (state.kind !== "property") {
      state.opsHub = false;
      state.tenantPortal = false;
      state.trust = false;
    }
    if (state.kind !== "practice") {
      state.practiceHub = false;
      state.practiceId = "";
    }
    if (state.tenantPortal) state.moov = true;
    if (state.trust) state.opsHub = false;
    if (state.opsHub || state.practiceHub) state.apiKey = true;
    if (!state.apiKey) state.statementsDownload = false;
  }

  function resetQuestionsForKind(): void {
    state.opsHub = false;
    state.tenantPortal = false;
    state.trust = false;
    state.practiceHub = false;
    state.practiceId = "";
    state.apiKey = false;
    state.statementsDownload = false;
    state.moov = false;
  }

  function paintName(): void {
    const host = h("section", { class: "card", "data-role": "add-company-name" });
    host.append(h("h2", {}, ["Name"]));

    const name = h("input", {
      type: "text",
      class: "input",
      "data-role": "company-legal-name",
      "aria-label": "Legal name",
      placeholder: "Synthetic Maple Rentals LLC",
      autocomplete: "off",
    });
    name.value = state.displayName;
    name.disabled = runCreated();
    name.addEventListener("input", () => {
      state.displayName = name.value;
      if (!state.slugEdited) {
        slugInputValue = suggestSlug(state.displayName);
        state.slug = slugInputValue;
        slugInput.value = slugInputValue;
      }
      scheduleSlugCheck();
    });

    const slugInput = h("input", {
      type: "text",
      class: "input",
      "data-role": "company-slug",
      "aria-label": "Web id",
      placeholder: "syn-maple-rentals",
      autocomplete: "off",
    });
    slugInput.value = state.slug;
    slugInput.disabled = runCreated();
    slugInput.addEventListener("input", () => {
      state.slugEdited = true;
      slugInputValue = slugInput.value.trim().toLowerCase();
      state.slug = slugInputValue;
      scheduleSlugCheck();
    });

    const slugStatus = h("p", {
      class: "muted",
      "data-role": "slug-status",
      "data-slug-state": state.slugState,
      role: "status",
    });
    const syncSlugStatus = (): void => {
      slugStatus.setAttribute("data-slug-state", state.slugState);
      clear(slugStatus);
      if (state.slug === "") {
        slugStatus.append(document.createTextNode("A web id is suggested from the name."));
        return;
      }
      if (state.slugState === "checking") {
        slugStatus.append(document.createTextNode("Checking this web id…"));
        return;
      }
      if (state.slugState === "free") {
        slugStatus.append(document.createTextNode("This web id is free."));
        return;
      }
      if (state.slugState === "invalid") {
        slugStatus.append(
          document.createTextNode(
            "Use lower-case letters, numbers and hyphens, starting with a letter.",
          ),
        );
        return;
      }
      if (state.slugState === "unknown") {
        slugStatus.append(
          document.createTextNode("Could not check this web id. Try again before continuing."),
        );
        return;
      }
      slugStatus.append(
        document.createTextNode(
          state.slugReason === "" ? "This web id is already taken." : state.slugReason,
        ),
      );
    };
    syncSlugStatus();

    host.append(
      h("label", { class: "field-label" }, ["Legal name", name]),
      h("label", { class: "field-label" }, ["Web id", slugInput]),
      slugStatus,
      ...(runCreated() ? [setupRecordLocked(), setupRecordExit()] : []),
      h("p", { class: "muted" }, [
        "The web id is used in the ledger name and the console. It cannot be changed later.",
      ]),
    );

    const back = h(
      "button",
      { type: "button", class: "btn btn-ghost", "data-action": "add-company-back" },
      ["Back"],
    );
    back.addEventListener("click", () => {
      step = 0;
      paint();
    });
    const next = h(
      "button",
      {
        type: "button",
        class: "btn btn-primary",
        "data-action": "add-company-next",
      },
      ["Continue"],
    );
    next.addEventListener("click", () => {
      if (!nameReady()) return;
      step = 2;
      paint();
    });

    refreshNameUi = (): void => {
      next.disabled = !nameReady();
      syncSlugStatus();
    };
    refreshNameUi();
    // The first render has no pending check; a free suggestion still needs one.
    if (state.slug !== "" && state.slugState === "empty") {
      scheduleSlugCheck();
    }
    host.append(h("div", { class: "card-actions" }, [back, next]));
    root.append(host);
  }

  function paintBooks(): void {
    const host = h("section", { class: "card", "data-role": "add-company-books" });
    host.append(h("h2", {}, ["How the books work"]));

    const basisHost = h("fieldset", { "data-role": "basis-choice" }, [
      h("legend", {}, ["Cash or accrual (required — no default)"]),
    ]);
    const accrualAllowed = operationalAccrualWritesAllowed();
    for (const value of ["cash", "accrual"] as const) {
      const input = h("input", {
        type: "radio",
        name: "add-company-basis",
        value,
        "data-role": `basis-${value}`,
      });
      input.checked = state.basis === value;
      // Accrual cannot be written until the accrual pack is on, and offering
      // it burned the web id: the run POST succeeded and the company POST then
      // refused with accrual_pack_not_ready (rd R11 R6-11).
      input.disabled = runCreated() || (value === "accrual" && !accrualAllowed);
      input.addEventListener("change", () => {
        state.basis = value;
        syncBooksNext();
      });
      basisHost.append(
        h("label", { class: "choice-label" }, [
          input,
          value === "cash" ? "Cash" : "Accrual",
          h("span", { class: "muted" }, [
            value === "cash"
              ? "Money counted when it moves."
              : accrualAllowed
                ? "Money counted when it is earned or owed."
                : "Accrual books are not built yet. This cannot be chosen.",
          ]),
        ]),
      );
    }
    host.append(basisHost);

    const booksStart = h("input", {
      type: "date",
      class: "input",
      "data-role": "books-start",
      "aria-label": "Books start date",
    });
    booksStart.value = state.booksStart;
    booksStart.disabled = runCreated();
    booksStart.addEventListener("input", () => {
      state.booksStart = booksStart.value;
      syncBooksNext();
    });
    if (runCreated()) host.append(setupRecordLocked(), setupRecordExit());
    host.append(
      h("label", { class: "field" }, [
        h("span", { class: "field-label" }, ["Books start"]),
        booksStart,
      ]),
    );

    const openingHost = h("fieldset", { "data-role": "opening-choice" }, [
      h("legend", {}, ["Opening balances (required — no default)"]),
    ]);
    const later = h("input", {
      type: "radio",
      name: "add-company-opening",
      value: "later",
      "data-role": "opening-later",
    });
    later.checked = state.opening === "later";
    later.addEventListener("change", () => {
      if (later.checked) {
        state.opening = "later";
      } else {
        state.openingAck = false;
      }
      paint();
    });
    const now = h("input", {
      type: "radio",
      name: "add-company-opening",
      value: "now",
      "data-role": "opening-now",
    });
    now.checked = state.opening === "now";
    now.addEventListener("change", () => {
      if (now.checked) {
        state.opening = "now";
        state.openingAck = false;
      }
      paint();
    });
    openingHost.append(
      h("label", { class: "choice-label" }, [later, "I'll provide them later (opening pending)"]),
      h("label", { class: "choice-label" }, [now, "I have them now (entered later)"]),
    );
    host.append(openingHost);

    if (state.opening === "later") {
      const ack = h("input", {
        type: "checkbox",
        "data-role": "opening-deferred-ack",
      });
      ack.checked = state.openingAck;
      ack.addEventListener("change", () => {
        state.openingAck = ack.checked;
        paint();
      });
      host.append(
        h("label", { class: "choice-label" }, [
          ack,
          " Opening books deferred — I acknowledge the opening balances are not provided now.",
        ]),
        h("p", { class: "muted" }, [
          "The checklist keeps an opening-balances row after go-live. Nothing is estimated or filled in for you.",
        ]),
      );
      if (state.openingAck) {
        host.append(
          h("p", { class: "muted", "data-role": "opening-deferred-ack-record" }, [
            "You ticked this on this page. It is not saved yet; the opening-books step will ask again.",
          ]),
        );
      }
    }
    if (state.opening === "now") {
      host.append(
        h("p", { class: "muted" }, [
          "After the company exists, someone completes the opening-books work. This page does not start it, and nothing is guessed.",
        ]),
      );
    }

    const back = h(
      "button",
      { type: "button", class: "btn btn-ghost", "data-action": "add-company-back" },
      ["Back"],
    );
    back.addEventListener("click", () => {
      step = 1;
      paint();
    });
    const next = h(
      "button",
      {
        type: "button",
        class: "btn btn-primary",
        "data-action": "add-company-next",
      },
      ["Continue"],
    );
    next.addEventListener("click", () => {
      if (!booksReady()) return;
      step = 3;
      paint();
    });
    host.append(h("div", { class: "card-actions" }, [back, next]));
    root.append(host);

    function syncBooksNext(): void {
      next.disabled = !booksReady();
    }
    syncBooksNext();
  }

  function paintReview(): void {
    const host = h("section", { class: "card", "data-role": "add-company-review" });
    host.append(h("h2", {}, ["Review"]));
    const summary = h("ul", { class: "review-summary", "data-role": "review-summary" });
    summary.append(
      h("li", {}, [`Kind: ${kindLabel(state.kind)}`]),
      h("li", {}, [`Name: ${amVisibleDisplayText(safeText(state.displayName))}`]),
      h("li", {}, [`Web id: ${safeText(state.slug)}`]),
      h("li", {}, [
        `Books: ${state.basis === "" ? "not chosen" : state.basis === "cash" ? "Cash" : "Accrual"}`,
      ]),
      h("li", {}, [`Books start: ${dateText(state.booksStart) || "not set"}`]),
      h("li", { "data-role": "review-opening" }, [
        `Opening balances: ${
          state.opening === ""
            ? "not chosen"
            : state.opening === "later"
              ? "provide later (ticked here; not saved yet)"
              : "have them now (entered later)"
        }`,
      ]),
      h("li", {}, [`Adds: ${capabilitySummary(buildCapabilities(state))}`]),
    );
    host.append(summary);

    host.append(
      h("h2", {}, ["What will happen now"]),
      h("p", { "data-role": "review-plan" }, [
        // Once the setup record exists, its creation is past tense: saying "we
        // create the onboarding record" beside "The setup record was already
        // created" contradicted itself (rd round 10 review, MUST-FIX 3).
        runCreated()
          ? "The onboarding record is created with these answers. When you press Finish registering, we register the company and open its checklist. Nothing else is created automatically from this screen."
          : "We create the onboarding record, register the company, and open its checklist. Nothing else is created automatically from this screen.",
      ]),
      h("h2", {}, ["What will need people afterwards"]),
      h("ul", { "data-role": "review-people" }, [
        h("li", {}, ["The ledger setup and checks need the system and then a person to review them."]),
        state.tenantPortal
          ? h("li", {}, ["The tenant portal team will set up the portal."])
          : null,
        state.opsHub ? h("li", {}, ["The hub team will map this company on its side."]) : null,
        state.apiKey ? h("li", {}, ["The owner issues the API key from the checklist."]) : null,
        state.opening === "later"
          ? h("li", {}, ["The account manager completes opening balances after go-live."])
          : h("li", {}, [
              "Someone completes the opening-books work after the company exists. This page does not start it.",
            ]),
      ]),
      h("p", { class: "muted", "data-role": "properties-note" }, [
        "Properties are set after the company exists. This flow does not collect them yet.",
      ]),
      ...(runCreated() ? [setupRecordLocked(), setupRecordExit()] : []),
    );

    const back = h(
      "button",
      { type: "button", class: "btn btn-ghost", "data-action": "add-company-back" },
      ["Back"],
    );
    back.addEventListener("click", () => {
      if (creating) return;
      step = 2;
      paint();
    });
    const create = h(
      "button",
      {
        type: "button",
        class: "btn btn-primary",
        "data-action": "create-company",
      },
      [runCreated() ? "Finish registering" : "Create company"],
    );
    create.disabled = !readyToCreate() || creating;
    create.addEventListener("click", () => {
      void runCreateCompany(create);
    });
    host.append(h("div", { class: "card-actions" }, [back, create]));
    if (createError !== null) {
      // The shared failure card always ends with "Nothing changed." That is
      // false once this page has created the setup record: the web id is
      // reserved even though no company was registered. `renderReceiptFailure`
      // takes no unchanged line, so the record case builds the same card shape
      // here rather than editing the shared card (round 3 review, MEDIUM 2).
      const card =
        runCreated() || createError.mayHaveLanded
          ? renderCreateFailure(
              createError.face,
              createError.unchanged ?? "The web id stays reserved.",
            )
          : renderReceiptFailure(createError.face, "Nothing was created.");
      if (createError.detail !== null) {
        card.append(
          h("details", { class: "disclosure", "data-role": "show-details" }, [
            h("summary", {}, ["Show details"]),
            h("p", {}, [createError.detail]),
          ]),
        );
      }
      // The trailing line is only there when it adds something the card does
      // not: "Reload this page" survives only for an outcome we could not read
      // at all. A taken web id has a real way back — Back while the answers are
      // editable, the setup-record exit once the record exists — so it does not
      // tell the person to reload (round 2 review, MEDIUM 2).
      if (createError.next === "press") {
        card.append(
          h("p", { class: "muted" }, [
            // Name the button the person can actually see (rd Z3: it read
            // "Create company" while the button said "Finish registering").
            runCreated()
              ? "Press Finish registering to try again."
              : "Press Create company to try again.",
          ]),
        );
      } else if (createError.next === "reload") {
        card.append(
          h("p", { class: "muted" }, ["Reload this page before trying again."]),
        );
      } else if (createError.next === "back") {
        card.append(
          h("p", { class: "muted" }, [
            // The web id is on the Name step, two cards before Review.
            "Press Back twice to change the web id.",
          ]),
        );
      }
      appendReceipt(host, card);
    }
    root.append(host);
  }

  /**
   * One key for the whole create: the run POST and the company POST are the
   * same logical action, so a retry after a lost answer is refused before it
   * can mint a second run or register twice (rd-r12 B3a-4).
   */
  const createKey = (): string =>
    `add-company:create:${slugInputValue}:${String(writePage)}`;

  /** The outcome words for a create that did not land. */
  function createFailure(error: unknown, outcome: WriteOutcome): {
    face: string;
    detail: string | null;
    mayHaveLanded: boolean;
    unchanged: string | null;
    next: "press" | "reload" | "back" | "none";
  } {
    const mayHaveLanded =
      outcome.kind === "unknown" || outcome.kind === "conflict";
    const detail = failureCopy(error, { role, context: "write" }).detail;
    // A completed 4xx refusal changed nothing, and the page can say which half
    // of the two-step create did not happen. A retry cannot pass a refused
    // validation, so the card invites no press.
    if (
      outcome.kind === "refused" &&
      outcome.error instanceof AdminApiError &&
      [400, 404, 422].includes(outcome.error.status)
    ) {
      return {
        face:
          createdRun === null
            ? "The company was not registered."
            : "The company was not registered. The setup record was created.",
        detail,
        mayHaveLanded: false,
        unchanged: createdRun === null ? null : "The web id stays reserved.",
        next: "none",
      };
    }
    const copy: FailureCopy =
      error === undefined
        ? { sentence: outcomeSentence(outcome, role), detail: null }
        : failureCopy(error, { role, context: "write" });
    return {
      face: copy.sentence,
      detail: copy.detail,
      mayHaveLanded,
      // The shared card closes with "Nothing changed.", which is false after a
      // request that may have landed (B3a-4).
      unchanged: mayHaveLanded
        ? "Check the company list before trying again."
        : null,
      // The sentence already names the way back; no second instruction.
      next: "none",
    };
  }

  /**
   * Adopt what our own lost request may have created. Never adopts a stranger's
   * record: a company must carry this form's name and kind, and a setup record
   * must carry these capabilities. Returns "company" when the company already
   * exists (the create is done), "run" when our setup record is there (register
   * the company next), "taken" when someone else holds the web id, and "none"
   * when the read could not answer.
   */
  async function recoverPossiblySent(
    answers: ReturnType<typeof buildAnswers>,
    capabilities: OnboardingCapabilities,
  ): Promise<"company" | "run" | "taken" | "none"> {
    const facts = await readSlugFacts();
    if (!stillCurrent() || facts === null) return "none";
    if (facts.company !== undefined) {
      if (facts.company !== null) {
        if (!companyIsOurs(facts.company, answers)) {
          reportTaken();
          return "taken";
        }
        createdRun = { answers, capabilities };
        forgetPossiblySent(createKey());
        opts.onCreated(state.slug);
        return "company";
      }
      // The server answered and the company is not there.
    }
    if (facts.run !== undefined && facts.run !== null) {
      if (!sameCapabilities(facts.run.capabilities, capabilities)) {
        reportTaken();
        return "taken";
      }
      createdRun = { answers, capabilities };
      forgetPossiblySent(createKey());
      return "run";
    }
    // Both reads answered and neither the company nor the setup record is
    // there: the earlier press never reached the server, so this press is an
    // ordinary first attempt. Forget the memory before returning, or
    // `blockWhenPossiblySent` tells the person a request "may already have
    // gone through" after the server has just said nothing exists — the dead
    // end this lane exists to remove (round 2 review, M1).
    if (facts.company === null && facts.run === null) {
      forgetPossiblySent(createKey());
    }
    return "none";
  }

  async function runCreateCompany(button: HTMLButtonElement): Promise<void> {
    if (!readyToCreate() || creating) return;
    // Accrual cannot be written until the pack is on (rd R11 R6-11). The
    // radio is disabled too, but a disabled control is not a check: refuse
    // here before the run record is minted (the run would succeed and the
    // company POST would then 422 `accrual_pack_not_ready`, burning the web
    // id). The capability module is the one the Worker and the Opening books
    // page consult, and it is never substituted here.
    if (state.basis === "accrual" && !operationalAccrualWritesAllowed()) {
      createError = {
        face: "Accrual books are not built yet. Choose Cash, then press Create company again.",
        detail: null,
        mayHaveLanded: false,
        unchanged: null,
        next: "press",
      };
      paint();
      return;
    }
    creating = true;
    createError = null;

    const answers = buildAnswers(state);
    const capabilities = buildCapabilities(state);
    const key = createKey();
    const scope = button.closest<HTMLElement>("section.card") ?? root;
    // A previous press may have reached the server without an answer. Read
    // what exists before the web-id pre-check can call our own record
    // "taken", and before a retry can mint a second run (B3a-4).
    if (wasPossiblySent(key)) {
      const recovered = await recoverPossiblySent(answers, capabilities);
      if (!stillCurrent()) return;
      if (recovered === "company" || recovered === "taken") {
        creating = false;
        return;
      }
    }
    // The web id was checked while the person typed; it can be taken before
    // they press the button. Re-run that check for a flow with no setup
    // record of its own yet, so a stranger's company or run is never adopted
    // (round 1 review, MEDIUM 3). A read that cannot answer does not block
    // the write: the recovery reads below still verify before adopting.
    if (createdRun === null) {
      const ownership = await slugOwnershipNow(answers);
      if (!stillCurrent()) return;
      if (ownership === "ours") {
        forgetPossiblySent(key);
        creating = false;
        opts.onCreated(state.slug);
        return;
      }
      if (ownership === "taken") {
        reportTaken();
        return;
      }
    }
    // Never register a company whose setup record is missing or was made
    // from different answers (A9-H1): the record is created first, for what
    // is about to be registered. On the ordinary path this is the same
    // single record the first attempt made.
    if (
      createdRun === null ||
      !sameAnswers(createdRun.answers, answers) ||
      !sameCapabilities(createdRun.capabilities, capabilities)
    ) {
      const run: OnboardingRunCreate = {
        slug: state.slug,
        mode: "new",
        answers,
        capabilities,
      };
      const outcome = await runWrite({
        key,
        scope,
        gate: () => null,
        send: () => client.createOnboardingRun(run),
        // A 409 or a lost answer can mean the run exists; confirm it is ours
        // before treating the write as landed.
        readBack: async () => {
          const facts = await readSlugFacts();
          return (
            facts !== null &&
            facts.run !== undefined &&
            facts.run !== null &&
            sameCapabilities(facts.run.capabilities, capabilities)
          );
        },
        stillCurrent,
        blockWhenPossiblySent: true,
      });
      if (outcome.kind === "landed") {
        createdRun = { answers, capabilities };
      } else {
        const recovered =
          outcome.kind === "unknown" || outcome.kind === "conflict"
            ? await recoverPossiblySent(answers, capabilities)
            : "none";
        if (!stillCurrent()) return;
        if (recovered === "company" || recovered === "taken") {
          creating = false;
          return;
        }
        if (recovered !== "run") {
          createError = createFailure(
            outcome.error,
            outcome,
          );
          creating = false;
          paint();
          return;
        }
      }
    }
    const outcome = await runWrite({
      key,
      scope,
      gate: () => null,
      send: () =>
        client.createCompany({
          ...answers,
          asDraft: false,
        }),
      readBack: async () => {
        const facts = await readSlugFacts();
        return (
          facts !== null &&
          facts.company !== undefined &&
          facts.company !== null &&
          companyIsOurs(facts.company, answers)
        );
      },
      stillCurrent,
      blockWhenPossiblySent: true,
    });
    if (outcome.kind === "landed") {
      forgetPossiblySent(key);
      creating = false;
      opts.onCreated(state.slug);
      return;
    }
    const recovered =
      outcome.kind === "unknown" || outcome.kind === "conflict"
        ? await recoverPossiblySent(answers, capabilities)
        : "none";
    if (!stillCurrent()) return;
    creating = false;
    if (recovered === "company" || recovered === "taken") return;
    createError = createFailure(outcome.error, outcome);
    paint();
  }

  /** Say the web id was taken before the write, and stop this attempt. */
  function reportTaken(): void {
    creating = false;
    state.slugState = "taken";
    state.slugReason = "";
    // Our own setup record may already exist when this is reached from the
    // recovery read after our own POST (a lost response, or the 409 that
    // follows it). Then "nothing was created" is false and the way out is the
    // setup-record exit the page already renders beside this receipt.
    createError = {
      face:
        createdRun === null
          ? "That web id was free when you started, but it has been taken since. Nothing of yours was registered. Choose a different web id."
          : "That web id was free when you started, but it has been taken since. The setup record was created. The company was not registered.",
      detail: null,
      mayHaveLanded: false,
      unchanged: null,
      // With a setup record, the create button is disabled and the answers are
      // frozen: the exit sentence is the only true instruction. Without one,
      // Back still edits the web id — twice, because the web id is two steps
      // before the Review card this receipt is drawn in.
      next: createdRun === null ? "back" : "none",
    };
    paint();
  }

  /**
   * Re-read the web id at press time. A read that failed is `undefined`, never
   * a "free" answer; `null` is the server saying the row is not there.
   */
  async function readSlugFacts(): Promise<{
    company: Company | null | undefined;
    run: OnboardingStatus | null | undefined;
  } | null> {
    const [companies, status] = await Promise.allSettled([
      client.listCompanies(),
      client.getOnboardingStatus(state.slug),
    ]);
    if (!stillCurrent()) return null;
    const company =
      companies.status === "fulfilled"
        ? (companies.value.companies.find(
            (row: Company) => row.slug === state.slug,
          ) ?? null)
        : undefined;
    let run: OnboardingStatus | null | undefined;
    if (status.status === "fulfilled") {
      run = status.value;
    } else if (
      status.reason instanceof AdminApiError &&
      status.reason.status === 404
    ) {
      run = null;
    } else {
      run = undefined;
    }
    if (company === undefined && run === undefined) return null;
    return { company, run };
  }

  /**
   * What the web id holds right now, from the person's point of view: `ours`
   * only for a registered company whose name and kind match the answers on
   * screen; `taken` for anything else.
   *
   * A setup record alone is `taken`, even when its capabilities match. The
   * status route returns capabilities but no answers, so a record found here
   * cannot be shown to hold the answers on this form — and registering the
   * company from those answers against a record we never checked is exactly
   * what A9-H1 forbids (round 3 review, HIGH 1). Our own lost response is a
   * different case: it is recovered after our own run POST, by the 409 read
   * in `createCompany`, where the record is the one we just tried to create.
   */
  async function slugOwnershipNow(
    answers: ReturnType<typeof buildAnswers>,
  ): Promise<"free" | "ours" | "taken" | "unknown"> {
    const facts = await readSlugFacts();
    if (facts === null) return "unknown";
    let answered = false;
    if (facts.company !== undefined) {
      answered = true;
      if (facts.company !== null) {
        return companyIsOurs(facts.company, answers) ? "ours" : "taken";
      }
    }
    if (facts.run !== undefined) {
      answered = true;
      if (facts.run !== null) return "taken";
    }
    return answered ? "free" : "unknown";
  }

  function scheduleSlugCheck(): void {
    if (slugTimer !== null) clearTimeout(slugTimer);
    const candidate = slugInputValue;
    if (candidate === "") {
      state.slugState = "empty";
      state.slugReason = "";
      refreshNameUi?.();
      return;
    }
    if (!SLUG_PATTERN.test(candidate) || candidate.length > 64) {
      state.slugState = "invalid";
      state.slugReason = "";
      refreshNameUi?.();
      return;
    }
    if (companySlugs.has(candidate)) {
      state.slugState = "taken";
      state.slugReason = "A company already uses this web id.";
      refreshNameUi?.();
      return;
    }
    if (draftSlugs.has(candidate)) {
      state.slugState = "taken";
      state.slugReason = "A saved draft already uses this web id.";
      refreshNameUi?.();
      return;
    }
    state.slugState = "checking";
    refreshNameUi?.();
    const seq = ++slugSeq;
    slugTimer = setTimeout(() => {
      void (async () => {
        try {
          await client.getOnboardingStatus(candidate);
          if (!stillCurrent() || seq !== slugSeq) return;
          state.slugState = "taken";
          state.slugReason = "An onboarding run already uses this web id.";
        } catch (error) {
          if (!stillCurrent() || seq !== slugSeq) return;
          if (error instanceof AdminApiError && error.status === 404) {
            state.slugState = "free";
            state.slugReason = "";
          } else {
            // Never claim "free" from a failed check.
            state.slugState = "unknown";
            state.slugReason = "";
          }
        }
        refreshNameUi?.();
      })();
    }, SLUG_CHECK_DELAY_MS);
  }

  function kindReady(): boolean {
    if (state.kind === "") return false;
    if (state.kind === "practice") {
      return !state.practiceHub || state.practiceId.trim() !== "";
    }
    return true;
  }

  function nameReady(): boolean {
    return (
      state.displayName.trim() !== "" &&
      state.slug !== "" &&
      state.slugState === "free"
    );
  }

  function booksReady(): boolean {
    if (state.basis === "") return false;
    if (!validDate(state.booksStart)) return false;
    if (state.opening === "") return false;
    if (state.opening === "later" && !state.openingAck) return false;
    return true;
  }

  function readyToCreate(): boolean {
    return kindReady() && nameReady() && booksReady() && !creating;
  }
}

function stepName(step: number): string {
  return ["What kind of company", "Name", "How the books work", "Review"][step] ?? "";
}

/**
 * The failure receipt with its own "nothing changed" line.
 *
 * `renderReceiptFailure` always ends "Nothing changed.", which is false once
 * this page has created the setup record: no company was registered, but the
 * web id is reserved. The shared card accepts no such line, so the record case
 * is built here in the same shape rather than editing the shared card (rd R11
 * R6b round 3 review, MEDIUM 2). Text nodes only.
 */
function renderCreateFailure(message: string, unchanged: string): HTMLElement {
  return h(
    "section",
    {
      class: "receipt-card banner-err",
      role: "alert",
      "data-role": "receipt",
      "data-status": "failed",
      tabindex: "-1",
    },
    [
      h("p", { class: "receipt-happened", "data-role": "receipt-happened" }, [
        h("span", { "aria-hidden": "true" }, ["✖ "]),
        amVisibleDisplayText(message),
      ]),
      h("p", { class: "receipt-unchanged muted", "data-role": "receipt-unchanged" }, [
        unchanged,
      ]),
    ],
  );
}

/**
 * The one plain sentence that explains the freeze (A9-H1). Once the setup
 * record exists, its answers are what the company will be registered with, so
 * they are read-only — Back still reads them.
 */
function setupRecordLocked(): HTMLElement {
  return h("p", {
    class: "muted",
    "data-role": "setup-record-locked",
  }, ["The setup record was already created with these answers."]);
}

/**
 * The way out when an answer is wrong after the setup record exists (rd round
 * 10 review, MUST-FIX 3). The console offers exactly one path, and this is it:
 * no route edits or deletes an onboarding run, so the web id the record holds
 * stays reserved; a fresh visit to Add a company can register the company
 * under a different web id. FLAGGED in the PR: there is no route that corrects
 * or releases a setup record.
 */
function setupRecordExit(): HTMLElement {
  return h("p", { class: "muted", "data-role": "setup-record-exit" }, [
    "If an answer is wrong, this web id stays reserved: nothing on this page can change it. To register the company with different answers, leave this page and add the company again with a different web id.",
  ]);
}

/** Do two answer sets describe the same setup record? */
function sameAnswers(
  a: ReturnType<typeof buildAnswers>,
  b: ReturnType<typeof buildAnswers>,
): boolean {
  return (
    a.slug === b.slug &&
    a.displayName === b.displayName &&
    a.companyType === b.companyType &&
    a.basis === b.basis &&
    a.hubLink === b.hubLink &&
    a.booksStart === b.booksStart &&
    a.featurePacks.length === b.featurePacks.length &&
    a.featurePacks.every((pack, index) => pack === b.featurePacks[index])
  );
}

/** Does a registered company carry the name and kind this form is about to register? */
function companyIsOurs(
  row: Company,
  answers: ReturnType<typeof buildAnswers>,
): boolean {
  return (
    row.displayName.trim() === answers.displayName &&
    row.companyType === answers.companyType
  );
}

/** The capabilities the setup record was made with (they are part of it). */
function sameCapabilities(
  a: OnboardingCapabilities,
  b: OnboardingCapabilities,
): boolean {
  return (
    a.kind === b.kind &&
    a.opsHub === b.opsHub &&
    a.practiceHub === b.practiceHub &&
    a.tenantPortal === b.tenantPortal &&
    a.moov === b.moov &&
    a.trust === b.trust &&
    a.apiKey === b.apiKey &&
    a.statementsDownload === b.statementsDownload
  );
}

function companyTypeFor(kind: Kind): CompanyType {
  return kind === "practice" ? "gvpm-practice" : "external";
}

function featurePacksFor(kind: Kind): CompanyFeaturePack[] {
  return kind === "practice" ? ["core", "medical"] : ["core"];
}

function buildCapabilities(state: FormState): OnboardingCapabilities {
  const kind: Kind = state.kind === "" ? "other" : state.kind;
  const moov = state.moov || state.tenantPortal;
  return {
    kind,
    opsHub: state.kind === "property" ? state.opsHub : false,
    practiceHub: state.kind === "practice" ? state.practiceHub : false,
    tenantPortal: state.kind === "property" ? state.tenantPortal : false,
    moov,
    trust: state.kind === "property" ? state.trust : false,
    apiKey: state.apiKey,
    statementsDownload: state.apiKey ? state.statementsDownload : false,
  };
}

function buildAnswers(state: FormState): {
  slug: CompanySlug;
  displayName: string;
  companyType: CompanyType;
  basis: CompanyBasis;
  featurePacks: CompanyFeaturePack[];
  hubLink: string | null;
  booksStart: string | null;
} {
  const kind: Kind = state.kind === "" ? "other" : state.kind;
  return {
    slug: state.slug,
    displayName: state.displayName.trim(),
    companyType: companyTypeFor(kind),
    basis: state.basis === "accrual" ? "accrual" : "cash",
    featurePacks: featurePacksFor(kind),
    hubLink:
      kind === "practice" && state.practiceHub ? state.practiceId.trim() : null,
    booksStart: validDate(state.booksStart) ? state.booksStart : null,
  };
}

function suggestSlug(displayName: string): string {
  const slug = displayName
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "-");
  if (slug === "") return "";
  return /^[a-z]/.test(slug) ? slug.slice(0, 64).replace(/-+$/g, "") : `co-${slug}`.slice(0, 64);
}

function validDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return false;
  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);
  const floor = new Date("2000-01-01T00:00:00Z");
  return parsed >= floor && parsed <= today;
}
